import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/api';

// Async thunks
export const fetchMenuItems = createAsyncThunk(
  'menu/fetchMenuItems',
  async (restaurantId, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const lastFetched = state.menu.lastFetched;
      const cacheAge = Date.now() - lastFetched;
      
      // Return cached data if it's less than 5 minutes old
      if (lastFetched && cacheAge < 5 * 60 * 1000) {
        return state.menu.items;
      }
      
      const response = await axios.get(`${API_ENDPOINTS.MENU}?restaurantId=${restaurantId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch menu items');
    }
  }
);

export const addMenuItem = createAsyncThunk(
  'menu/addMenuItem',
  async (menuItem, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_ENDPOINTS.MENU_ADD, menuItem);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add menu item');
    }
  }
);

export const updateMenuItem = createAsyncThunk(
  'menu/updateMenuItem',
  async ({ id, updates }, { rejectWithValue }) => {
    try {
      const response = await axios.put(API_ENDPOINTS.MENU_ITEM(id), updates);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update menu item');
    }
  }
);

export const deleteMenuItem = createAsyncThunk(
  'menu/deleteMenuItem',
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(API_ENDPOINTS.MENU_ITEM(id));
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete menu item');
    }
  }
);

export const toggleMenuItemAvailability = createAsyncThunk(
  'menu/toggleMenuItemAvailability',
  async (id, { rejectWithValue, getState }) => {
    try {
      const response = await axios.put(API_ENDPOINTS.MENU_TOGGLE_AVAILABILITY(id));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to toggle availability');
    }
  }
);

export const toggleQuickServe = createAsyncThunk(
  'menu/toggleQuickServe',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.put(API_ENDPOINTS.MENU_TOGGLE_QUICK_SERVE(id));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to toggle quick serve');
    }
  }
);

const initialState = {
  items: [],
  loading: false,
  error: null,
  lastFetched: null,
  filters: {
    category: 'all',
    type: 'all',
    search: '',
    availability: 'all',
  },
  categories: [],
  types: [],
};

const menuSlice = createSlice({
  name: 'menu',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        category: 'all',
        type: 'all',
        search: '',
        availability: 'all',
      };
    },
    setSearchTerm: (state, action) => {
      state.filters.search = action.payload;
    },
    invalidateCache: (state) => {
      state.lastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Menu Items
      .addCase(fetchMenuItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMenuItems.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
        state.lastFetched = Date.now();
        
        // Extract unique categories and types
        const categories = [...new Set(action.payload.map(item => item.itemCategory))];
        const types = [...new Set(action.payload.map(item => item.itemType))];
        state.categories = categories;
        state.types = types;
      })
      .addCase(fetchMenuItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Add Menu Item
      .addCase(addMenuItem.fulfilled, (state, action) => {
        state.items.push(action.payload);
        state.lastFetched = Date.now();
      })
      
      // Update Menu Item
      .addCase(updateMenuItem.fulfilled, (state, action) => {
        const index = state.items.findIndex(item => item._id === action.payload._id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
        state.lastFetched = Date.now();
      })
      
      // Delete Menu Item
      .addCase(deleteMenuItem.fulfilled, (state, action) => {
        state.items = state.items.filter(item => item._id !== action.payload);
        state.lastFetched = Date.now();
      })
      
      // Toggle Availability
      .addCase(toggleMenuItemAvailability.fulfilled, (state, action) => {
        const index = state.items.findIndex(item => item._id === action.payload._id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      
      // Toggle Quick Serve
      .addCase(toggleQuickServe.fulfilled, (state, action) => {
        const index = state.items.findIndex(item => item._id === action.payload._id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      });
  },
});

// Selectors
export const selectAllMenuItems = (state) => state.menu.items;
export const selectMenuLoading = (state) => state.menu.loading;
export const selectMenuError = (state) => state.menu.error;
export const selectMenuFilters = (state) => state.menu.filters;
export const selectMenuCategories = (state) => state.menu.categories;
export const selectMenuTypes = (state) => state.menu.types;

export const selectFilteredMenuItems = (state) => {
  const { items, filters } = state.menu;
  
  return items.filter(item => {
    const categoryMatch = filters.category === 'all' || 
      item.itemCategory?.toLowerCase() === filters.category.toLowerCase();
    const typeMatch = filters.type === 'all' || 
      item.itemType?.toLowerCase() === filters.type.toLowerCase();
    const availabilityMatch = filters.availability === 'all' || 
      (filters.availability === 'available' ? item.isAvailable : !item.isAvailable);
    const searchMatch = !filters.search || 
      item.itemName?.toLowerCase().includes(filters.search.toLowerCase()) ||
      item.itemDescription?.toLowerCase().includes(filters.search.toLowerCase());
    
    return categoryMatch && typeMatch && availabilityMatch && searchMatch;
  });
};

export const selectTodaysSpecials = (state) => 
  state.menu.items.filter(item => item.itemCategory === 'todaysSpecial');

export const selectQuickServeItems = (state) => 
  state.menu.items.filter(item => item.quickServe);

export const selectAvailableItems = (state) => 
  state.menu.items.filter(item => item.isAvailable);

export const { 
  clearError, 
  setFilters, 
  clearFilters, 
  setSearchTerm, 
  invalidateCache 
} = menuSlice.actions;

export default menuSlice.reducer;

